import { sortByName } from '../../utils/sortOptions';
import PasswordInput from '../../components/PasswordInput';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  MagnifyingGlassIcon, PencilIcon, TrashIcon, XMarkIcon,
  CheckCircleIcon, NoSymbolIcon, FunnelIcon,
  UserGroupIcon, BanknotesIcon, AcademicCapIcon,
} from '@heroicons/react/24/outline';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .sf * { box-sizing:border-box; }
  .sf { font-family:'Sora',sans-serif; color:#1a1d2e; animation:sfFade .4s ease both; }
  @keyframes sfFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:none} }
  .sf-title { font-size:1.35rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; }
  .sf-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; margin-bottom:1.5rem; }
  /* Stat cards */
  .sf-stats { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-bottom:1.5rem; }
  .sf-stat  { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1rem 1.1rem; display:flex; align-items:center; gap:12px; }
  .sf-stat-icon { width:38px; height:38px; border-radius:10px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .sf-stat-icon svg { width:18px; height:18px; stroke-width:1.8; }
  .sf-stat-icon.purple { background:#f3f0ff; color:#8b5cf6; }
  .sf-stat-icon.green  { background:#ecfdf5; color:#10c98e; }
  .sf-stat-icon.blue   { background:#eef2ff; color:#3b6ef8; }
  .sf-stat-label { font-size:.68rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .sf-stat-val   { font-size:1.3rem; font-weight:700; color:#0f1123; font-family:'JetBrains Mono',monospace; }
  /* Tabs */
  .sf-tabs { display:flex; gap:6px; margin-bottom:1.2rem; }
  .sf-tab  { display:inline-flex; align-items:center; gap:7px; padding:8px 18px; border-radius:10px; border:1.5px solid #e0e4f0; background:white; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; color:#6b7194; transition:all .15s; }
  .sf-tab svg { width:15px; height:15px; stroke-width:2; }
  .sf-tab.coord.active  { background:#8b5cf6; color:white; border-color:#8b5cf6; }
  .sf-tab.finance.active{ background:#10c98e; color:white; border-color:#10c98e; }
  .sf-tab:not(.active):hover { border-color:#8b5cf6; color:#8b5cf6; }
  /* Toolbar */
  .sf-toolbar { display:flex; gap:.7rem; flex-wrap:wrap; margin-bottom:1.2rem; align-items:center; }
  .sf-search-wrap { position:relative; flex:1; min-width:200px; max-width:340px; }
  .sf-search-wrap svg { position:absolute; left:12px; top:50%; transform:translateY(-50%); width:15px; height:15px; stroke:#9094a8; pointer-events:none; }
  .sf-search { font-family:'Sora',sans-serif; font-size:.8rem; width:100%; padding:8px 12px 8px 34px; border:1.5px solid #e4e7f0; border-radius:10px; background:white; color:#1a1d2e; outline:none; }
  .sf-search:focus { border-color:#8b5cf6; }
  .sf-select { font-family:'Sora',sans-serif; font-size:.78rem; padding:8px 12px; border:1.5px solid #e0e4f0; border-radius:10px; background:white; color:#1a1d2e; outline:none; }
  /* Table */
  .sf-panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  .sf-tbl-wrap { overflow-x:auto; }
  .sf-tbl { width:100%; border-collapse:collapse; }
  .sf-tbl thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .sf-tbl th { padding:10px 16px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .sf-tbl tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
  .sf-tbl tbody tr:last-child { border-bottom:none; }
  .sf-tbl tbody tr:hover { background:#fafbff; }
  .sf-tbl td { padding:11px 16px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .sf-avatar { width:34px; height:34px; border-radius:9px; display:flex; align-items:center; justify-content:center; font-size:.74rem; font-weight:700; color:white; flex-shrink:0; }
  .sf-avatar.coordinator { background:linear-gradient(135deg,#8b5cf6,#6d28d9); }
  .sf-avatar.finance     { background:linear-gradient(135deg,#10c98e,#059669); }
  .role-pill { font-size:.65rem; font-weight:700; padding:3px 10px; border-radius:99px; display:inline-flex; white-space:nowrap; }
  .role-pill.coordinator { background:#f3f0ff; color:#8b5cf6; }
  .role-pill.finance     { background:#ecfdf5; color:#10c98e; }
  .status-pill { display:inline-flex; align-items:center; gap:5px; font-size:.74rem; font-weight:600; white-space:nowrap; }
  .status-dot  { width:6px; height:6px; border-radius:50%; flex-shrink:0; }
  .action-btns { display:flex; gap:5px; align-items:center; }
  .btn-action { width:28px; height:28px; border-radius:7px; border:1px solid #e4e7f0; background:white; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all .15s; flex-shrink:0; }
  .btn-action svg { width:13px; height:13px; stroke-width:2; }
  .btn-action.edit:hover   { background:#eef2ff; border-color:#3b6ef8; color:#3b6ef8; }
  .btn-action.del:hover    { background:#fef2f2; border-color:#fecaca; color:#ef4444; }
  .btn-action.deactivate   { color:#ef4444; border-color:#fecaca; background:#fef2f2; }
  .btn-action.deactivate:hover { background:#ef4444; color:white; }
  .btn-action.activate     { color:#10c98e; border-color:#a7f3d0; background:#ecfdf5; }
  .btn-action.activate:hover   { background:#10c98e; color:white; }
  .btn-action:disabled     { opacity:.5; cursor:not-allowed; }
  /* Pagination */
  .sf-pag { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-top:1px solid #f3f5fb; }
  .sf-pag-info { font-size:.74rem; color:#9094a8; }
  .sf-pag-btns { display:flex; gap:5px; }
  .sf-pag-btn  { width:30px; height:30px; border-radius:8px; border:1px solid #e4e7f0; background:white; cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:.78rem; font-weight:600; color:#6b7194; }
  .sf-pag-btn.active { background:#8b5cf6; color:white; border-color:#8b5cf6; }
  .sf-pag-btn:disabled { opacity:.4; cursor:not-allowed; }
  /* Empty */
  .sf-empty { text-align:center; padding:3rem 2rem; }
  .sf-empty-icon { width:52px; height:52px; border-radius:14px; background:#f3f0ff; color:#8b5cf6; display:flex; align-items:center; justify-content:center; margin:0 auto 12px; }
  .sf-empty-icon svg { width:24px; height:24px; stroke-width:1.6; }
  .sf-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .sf-spin { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#8b5cf6; border-radius:50%; animation:sp .7s linear infinite; }
  @keyframes sp { to{transform:rotate(360deg)} }
  /* Edit Modal */
  .mb { position:fixed; inset:0; background:rgba(10,12,28,.65); backdrop-filter:blur(5px); display:flex; align-items:center; justify-content:center; z-index:9999; padding:20px; }
  .mo { background:white; border-radius:20px; width:100%; max-width:460px; display:flex; flex-direction:column; box-shadow:0 25px 60px rgba(0,0,0,.25); animation:moIn .25s ease; overflow:hidden; }
  @keyframes moIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .mo-hd { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f2f8; }
  .mo-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .mo-x { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .mo-x svg { width:14px; height:14px; stroke-width:2; }
  .mo-bd { padding:20px 22px; }
  .mo-ft { display:flex; gap:8px; justify-content:flex-end; padding:14px 22px; border-top:1px solid #f0f2f8; }
  .ff { display:flex; flex-direction:column; gap:5px; margin-bottom:12px; }
  .fl { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .fi { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .fi:focus { border-color:#8b5cf6; background:white; }
  .btn-save  { padding:9px 20px; border:none; border-radius:10px; background:#8b5cf6; color:white; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; }
  .btn-save:disabled { opacity:.6; cursor:not-allowed; }
  .btn-ghost { padding:9px 18px; border:none; border-radius:10px; background:#f0f2f7; color:#6b7194; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; }

  /* Mobile cards */
  .sf-cards { display:none; padding:10px; }
  .sf-card { background:white; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .sf-card-top { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:8px; }
  .sf-card-row { display:flex; justify-content:space-between; align-items:center; font-size:.78rem; padding:5px 0; border-bottom:1px solid #f3f5fb; gap:8px; }
  .sf-card-row:last-of-type { border-bottom:none; }
  .sf-card-label { color:#9094a8; font-weight:600; font-size:.68rem; text-transform:uppercase; letter-spacing:.05em; flex-shrink:0; }
  .sf-card-actions { display:flex; gap:6px; margin-top:10px; }
  @media (max-width:768px) {
    .sf-stats { grid-template-columns:1fr 1fr; }
    .sf-tbl-wrap { display:none !important; }
    .sf-cards { display:block; }
  }
`;

const initials = n => n?.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase() || '?';

export default function SuperStaff() {
  const [users,       setUsers]       = useState([]);
  const [schools,     setSchools]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [tab,         setTab]         = useState('coordinator'); // coordinator | finance
  const [search,      setSearch]      = useState('');
  const [schoolFilter,setSchoolFilter]= useState('');
  const [page,        setPage]        = useState(1);
  const [totalPages,  setTotalPages]  = useState(1);
  const [total,       setTotal]       = useState(0);
  const [toggling,    setToggling]    = useState({});
  const [editTarget,  setEditTarget]  = useState(null);
  const [editData,    setEditData]    = useState({});
  const [submitting,  setSubmitting]  = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ role: tab, page, limit: 15 });
      if (schoolFilter) params.append('school', schoolFilter);
      const { data } = await api.get(`/super/users?${params}`);
      setUsers(data.users || []);
      setTotalPages(data.totalPages || 1);
      setTotal(data.total || 0);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, [tab, page, schoolFilter]);
  useEffect(() => { setPage(1); fetchUsers(); }, [tab, schoolFilter]);

  useEffect(() => {
    api.get('/super/schools').then(r => setSchools(r.data?.schools || r.data || [])).catch(() => {});
  }, []);

  const filtered = users.filter(u =>
    !search ||
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const handleToggle = async (userId) => {
    setToggling(p => ({ ...p, [userId]: true }));
    try {
      const { data } = await api.put(`/super/users/${userId}/toggle-active`);
      toast.success(data.message);
      fetchUsers();
    } catch { toast.error('Failed'); }
    finally { setToggling(p => ({ ...p, [userId]: false })); }
  };

  const openEdit = (user) => {
    setEditTarget(user);
    setEditData({ name: user.name, email: user.email, isActive: user.isActive ?? true });
  };

  const handleSave = async () => {
    setSubmitting(true);
    try {
      await api.put(`/super/users/${editTarget._id}`, editData);
      toast.success('Updated successfully');
      setEditTarget(null);
      fetchUsers();
    } catch { toast.error('Failed to update'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete ${name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/super/users/${id}`);
      toast.success(`${name} deleted`);
      fetchUsers();
    } catch { toast.error('Failed to delete'); }
  };

  // Count by role for stat cards
  const coordCount = tab === 'coordinator' ? total : '—';
  const financeCount = tab === 'finance' ? total : '—';

  return (
    <div className="sf"><style>{css}</style>

      <div className="sf-title">Finance & Coordinators</div>
      <div className="sf-sub">Manage coordinator and finance staff across all schools — edit, activate, deactivate or remove any user.</div>

      {/* Stat cards */}
      <div className="sf-stats">
        <div className="sf-stat">
          <div className="sf-stat-icon purple"><AcademicCapIcon /></div>
          <div>
            <div className="sf-stat-label">Coordinators</div>
            <div className="sf-stat-val">{tab === 'coordinator' ? total : '—'}</div>
          </div>
        </div>
        <div className="sf-stat">
          <div className="sf-stat-icon green"><BanknotesIcon /></div>
          <div>
            <div className="sf-stat-label">Finance Staff</div>
            <div className="sf-stat-val">{tab === 'finance' ? total : '—'}</div>
          </div>
        </div>
        <div className="sf-stat">
          <div className="sf-stat-icon blue"><UserGroupIcon /></div>
          <div>
            <div className="sf-stat-label">Showing</div>
            <div className="sf-stat-val">{total}</div>
          </div>
        </div>
      </div>

      {/* Role tabs */}
      <div className="sf-tabs">
        <button className={`sf-tab coord ${tab==='coordinator'?'active':''}`} onClick={() => { setTab('coordinator'); setPage(1); }}>
          <AcademicCapIcon />Coordinators
        </button>
        <button className={`sf-tab finance ${tab==='finance'?'active':''}`} onClick={() => { setTab('finance'); setPage(1); }}>
          <BanknotesIcon />Finance
        </button>
      </div>

      {/* Toolbar */}
      <div className="sf-toolbar">
        <div className="sf-search-wrap">
          <MagnifyingGlassIcon />
          <input className="sf-search" placeholder={`Search ${tab}s…`}
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="sf-select" value={schoolFilter} onChange={e => setSchoolFilter(e.target.value)}>
          <option value="">All Schools</option>
          {sortByName(schools).map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="sf-panel">
        {loading ? (
          <div className="sf-loading"><div className="sf-spin"/>Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="sf-empty">
            <div className="sf-empty-icon"><UserGroupIcon /></div>
            <div style={{fontWeight:600,color:'#1a1d2e',marginBottom:4}}>No {tab}s found</div>
            <div style={{fontSize:'.76rem',color:'#9094a8'}}>
              {search ? 'Try a different search.' : `No ${tab} accounts exist yet.`}
            </div>
          </div>
        ) : (
          <>
            <div className="sf-tbl-wrap">
              <table className="sf-tbl">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>School</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(user => (
                    <tr key={user._id}>
                      {/* Name */}
                      <td>
                        <div style={{display:'flex',alignItems:'center',gap:9}}>
                          <div className={`sf-avatar ${user.role}`}>{initials(user.name)}</div>
                          <div>
                            <div style={{fontWeight:600,color:'#0f1123'}}>{user.name}</div>
                            <div style={{fontSize:'.68rem',color:'#9094a8',fontFamily:"'JetBrains Mono',monospace"}}>{String(user._id ?? '').slice(-6)}</div>
                          </div>
                        </div>
                      </td>
                      {/* Email */}
                      <td style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.75rem',color:'#6b7194'}}>
                        {user.email}
                      </td>
                      {/* Role */}
                      <td>
                        <span className={`role-pill ${user.role}`}>{user.role}</span>
                      </td>
                      {/* School */}
                      <td style={{fontSize:'.78rem',color:'#6b7194'}}>
                        {user.school?.name || <span style={{color:'#c0c5dc'}}>—</span>}
                      </td>
                      {/* Status */}
                      <td>
                        <span className="status-pill" style={{color: user.isActive!==false ? '#10c98e' : '#ef4444'}}>
                          <span className="status-dot" style={{background: user.isActive!==false ? '#10c98e' : '#ef4444'}}/>
                          {user.isActive!==false ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      {/* Actions */}
                      <td>
                        <div className="action-btns">
                          <button
                            className={`btn-action ${user.isActive!==false ? 'deactivate' : 'activate'}`}
                            onClick={() => handleToggle(user._id)}
                            disabled={toggling[user._id]}
                            title={user.isActive!==false ? 'Deactivate' : 'Activate'}>
                            {toggling[user._id] ? '…' : user.isActive!==false ? <NoSymbolIcon /> : <CheckCircleIcon />}
                          </button>
                          <button className="btn-action edit" onClick={() => openEdit(user)} title="Edit">
                            <PencilIcon />
                          </button>
                          <button className="btn-action del" onClick={() => handleDelete(user._id, user.name)} title="Delete">
                            <TrashIcon />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="sf-cards">
              {filtered.map(user => (
                <div className="sf-card" key={user._id}>
                  <div className="sf-card-top">
                    <div style={{display:'flex',alignItems:'center',gap:9}}>
                      <div className={`sf-avatar ${user.role}`}>{initials(user.name)}</div>
                      <div>
                        <div style={{fontWeight:600,color:'#0f1123'}}>{user.name}</div>
                        <div style={{fontSize:'.68rem',color:'#9094a8',fontFamily:"'JetBrains Mono',monospace"}}>{String(user._id ?? '').slice(-6)}</div>
                      </div>
                    </div>
                    <span className={`role-pill ${user.role}`}>{user.role}</span>
                  </div>
                  <div className="sf-card-row"><span className="sf-card-label">Email</span><span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.75rem',color:'#6b7194'}}>{user.email}</span></div>
                  <div className="sf-card-row"><span className="sf-card-label">School</span><span style={{fontSize:'.78rem',color:'#6b7194'}}>{user.school?.name || <span style={{color:'#c0c5dc'}}>—</span>}</span></div>
                  <div className="sf-card-row"><span className="sf-card-label">Status</span>
                    <span className="status-pill" style={{color: user.isActive!==false ? '#10c98e' : '#ef4444'}}>
                      <span className="status-dot" style={{background: user.isActive!==false ? '#10c98e' : '#ef4444'}}/>
                      {user.isActive!==false ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="sf-card-actions">
                    <button
                      className={`btn-action ${user.isActive!==false ? 'deactivate' : 'activate'}`}
                      onClick={() => handleToggle(user._id)}
                      disabled={toggling[user._id]}
                      title={user.isActive!==false ? 'Deactivate' : 'Activate'}>
                      {toggling[user._id] ? '…' : user.isActive!==false ? <NoSymbolIcon /> : <CheckCircleIcon />}
                    </button>
                    <button className="btn-action edit" onClick={() => openEdit(user)} title="Edit">
                      <PencilIcon />
                    </button>
                    <button className="btn-action del" onClick={() => handleDelete(user._id, user.name)} title="Delete">
                      <TrashIcon />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="sf-pag">
                <div className="sf-pag-info">Page {page} of {totalPages} · {total} total</div>
                <div className="sf-pag-btns">
                  <button className="sf-pag-btn" disabled={page===1} onClick={() => setPage(p=>p-1)}>‹</button>
                  {Array.from({length: Math.min(totalPages,5)},(_,i)=>i+1).map(p => (
                    <button key={p} className={`sf-pag-btn ${p===page?'active':''}`} onClick={() => setPage(p)}>{p}</button>
                  ))}
                  <button className="sf-pag-btn" disabled={page===totalPages} onClick={() => setPage(p=>p+1)}>›</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Modal */}
      {editTarget && createPortal(
        <div className="mb" onClick={() => setEditTarget(null)}>
          <div className="mo" onClick={e => e.stopPropagation()}>
            <div className="mo-hd">
              <div className="mo-title">Edit {editTarget.role} — {editTarget.name}</div>
              <button className="mo-x" onClick={() => setEditTarget(null)}><XMarkIcon /></button>
            </div>
            <div className="mo-bd">
              <div className="ff">
                <label className="fl">Full Name</label>
                <input className="fi" value={editData.name||''} onChange={e => setEditData(p=>({...p,name:e.target.value}))} placeholder="Name" />
              </div>
              <div className="ff">
                <label className="fl">Email</label>
                <input type="email" className="fi" value={editData.email||''} onChange={e => setEditData(p=>({...p,email:e.target.value}))} placeholder="Email" />
              </div>
              <div className="ff">
                <label className="fl">New Password (leave blank to keep)</label>
                <PasswordInput className="fi" value={editData.password||''} onChange={e => setEditData(p=>({...p,password:e.target.value}))} placeholder="New password…" />
              </div>
              <div className="ff" style={{flexDirection:'row',alignItems:'center',gap:10}}>
                <input type="checkbox" id="isActive" checked={editData.isActive} onChange={e => setEditData(p=>({...p,isActive:e.target.checked}))} style={{width:16,height:16,cursor:'pointer'}} />
                <label htmlFor="isActive" className="fl" style={{margin:0,cursor:'pointer'}}>Active Account</label>
              </div>
            </div>
            <div className="mo-ft">
              <button className="btn-ghost" onClick={() => setEditTarget(null)}>Cancel</button>
              <button className="btn-save" onClick={handleSave} disabled={submitting}>{submitting ? 'Saving…' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}